import { z } from 'zod'

// Typed env loader — see ARCHITECTURE.md §9 + BLOCKER-PROTOCOL.md
// Required vars fail loudly at startup; optional vars derive *_ENABLED flags.
// Every third-party key is OPTIONAL — the app runs fully with mocks when absent.

const envSchema = z.object({
  // ─── Required (Supabase) ──────────────────────────────────────
  VITE_SUPABASE_URL: z.string().url().default('https://placeholder.supabase.co'),
  VITE_SUPABASE_ANON_KEY: z.string().min(1).default('placeholder-anon-key'),

  // ─── Optional third-party keys ────────────────────────────────
  // Stripe (needs all three for real mode — server keys checked server-side)
  VITE_STRIPE_PUBLISHABLE_KEY: z.string().optional(),

  // Criipto / BankID
  VITE_CRIIPTO_DOMAIN: z.string().optional(),
  VITE_CRIIPTO_CLIENT_ID: z.string().optional(),

  // Mapbox (geocoding + maps)
  VITE_MAPBOX_TOKEN: z.string().optional(),

  // Turnstile (CAPTCHA)
  VITE_TURNSTILE_SITE_KEY: z.string().optional(),

  // Klarna (Pay Later) — publishable client key for widget bootstrapping.
  // Username/password are server-only and live in supabase/functions/_shared.
  VITE_KLARNA_CLIENT_KEY: z.string().optional(),
  VITE_KLARNA_REGION: z.enum(['eu', 'na', 'oc']).optional().default('eu'),

  // Observability
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_POSTHOG_KEY: z.string().optional(),
  VITE_POSTHOG_HOST: z.string().optional(),

  // Mode flags
  MODE: z.string().default('development'),
  DEV: z.boolean().default(true),
  PROD: z.boolean().default(false),
})

const parsed = envSchema.safeParse(import.meta.env)

if (!parsed.success) {
  console.error('❌ Invalid environment variables:', parsed.error.flatten().fieldErrors)
  throw new Error('Invalid environment variables — check .env.local against .env.example')
}

const data = parsed.data

// ─── Derived flags ────────────────────────────────────────────────
// Each *_ENABLED flag is true only when all required keys for that
// service are present. See BLOCKER-PROTOCOL.md for per-service specs.
//
// Note: server-only services (Resend, Twilio, Anthropic, Bolagsverket)
// have their flags derived on the server in supabase/functions/_shared/env.ts.
// Client-side flags only cover keys prefixed with VITE_.
export const env = {
  ...data,

  isProd: data.PROD,
  isDev: data.DEV,

  // Supabase always available (required)
  SUPABASE_URL: data.VITE_SUPABASE_URL,
  SUPABASE_ANON_KEY: data.VITE_SUPABASE_ANON_KEY,

  // Dev escape hatch — true when no real Supabase project is configured.
  // Enables a "Continue as test seller/customer" bypass on auth pages and makes
  // data hooks return empty so the UI is still browseable without a backend.
  // Requires BOTH sentinel values so a half-configured .env.local (where the
  // user set the URL but not the anon key, or vice versa) does NOT silently
  // drop into dev-bypass mode and hide a real configuration error.
  IS_PLACEHOLDER_BACKEND:
    data.VITE_SUPABASE_URL === 'https://placeholder.supabase.co' &&
    data.VITE_SUPABASE_ANON_KEY === 'placeholder-anon-key',

  // ─── Per-service enabled flags (BLOCKER-PROTOCOL.md) ──────────
  // Stripe: real when publishable key present (server also checks secret + webhook)
  STRIPE_ENABLED: Boolean(data.VITE_STRIPE_PUBLISHABLE_KEY),

  // Criipto / BankID: real when domain + client ID present
  CRIIPTO_ENABLED: Boolean(data.VITE_CRIIPTO_DOMAIN && data.VITE_CRIIPTO_CLIENT_ID),

  // Mapbox: real when token present
  MAPBOX_ENABLED: Boolean(data.VITE_MAPBOX_TOKEN),

  // Turnstile: real when site key present (server also checks secret)
  TURNSTILE_ENABLED: Boolean(data.VITE_TURNSTILE_SITE_KEY),

  // Klarna: real when client key present (server also checks username/password)
  KLARNA_ENABLED: Boolean(data.VITE_KLARNA_CLIENT_KEY),
  KLARNA_CLIENT_KEY: data.VITE_KLARNA_CLIENT_KEY ?? null,
  KLARNA_REGION: data.VITE_KLARNA_REGION,

  // Observability (not mock/real — just enabled/disabled)
  SENTRY_ENABLED: Boolean(data.VITE_SENTRY_DSN),
  POSTHOG_ENABLED: Boolean(data.VITE_POSTHOG_KEY),

  // Server-only services — always false client-side; real flags live server-side.
  // Included here so components can import a single env object with all flags.
  RESEND_ENABLED: false as boolean,
  TWILIO_ENABLED: false as boolean,
  ANTHROPIC_ENABLED: false as boolean,
  BOLAGSVERKET_ENABLED: false as boolean,
} as const
